import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";

export function FieldTechProfile() {
  const isMobile = useIsMobile();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Edit form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/auth/me/`);
      setProfile(res.data);
      setFirstName(res.data.first_name);
      setLastName(res.data.last_name);
      setEmail(res.data.email);
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      const res = await axios.patch(`${BASE_URL}/auth/me/`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
      });
      setProfile(res.data);
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError("");
    setPasswordSuccess(false);
    if (!currentPassword) {
      setPasswordError("Please enter your current password.");
      return;
    }
    if (!newPassword) {
      setPasswordError("Please enter a new password.");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${BASE_URL}/auth/change-password/`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordForm(false);
      setTimeout(() => setPasswordSuccess(false), 4000);
    } catch (err) {
      setPasswordError(
        err.response?.data?.error ||
          "Failed to change password. Please try again.",
      );
    } finally {
      setChangingPassword(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    backgroundColor: colors.white,
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "700",
    color: colors.slate,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
    display: "block",
  };

  const fieldStyle = { marginBottom: "16px" };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "10px",
    padding: "20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: "16px",
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "16px" : "28px",
        maxWidth: "560px",
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <h1
        style={{
          fontSize: "20px",
          fontWeight: "700",
          color: colors.darkGrey,
          marginBottom: "24px",
        }}
      >
        My Profile
      </h1>

      {/* Profile card */}
      <div style={cardStyle}>
        {/* Avatar */}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              backgroundColor: colors.teal,
              color: "white",
              fontSize: "28px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 10px",
            }}
          >
            {(
              profile.first_name?.[0] ||
              profile.username?.[0] ||
              "?"
            ).toUpperCase()}
          </div>
          <div
            style={{
              fontWeight: "700",
              fontSize: "16px",
              color: colors.darkGrey,
            }}
          >
            {profile.first_name} {profile.last_name}
          </div>
          <div style={{ fontSize: "13px", color: colors.slate }}>
            @{profile.username}
          </div>
        </div>

        {saveSuccess && (
          <div
            style={{
              backgroundColor: "#e8f8f1",
              border: `1px solid ${colors.green}`,
              borderRadius: "6px",
              padding: "10px 14px",
              color: colors.green,
              fontSize: "13px",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            ✅ Profile updated successfully
          </div>
        )}

        {!editing ? (
          <>
            <div style={fieldStyle}>
              <label style={labelStyle}>First Name</label>
              <div
                style={{
                  fontSize: "14px",
                  color: colors.text,
                  padding: "10px 0",
                }}
              >
                {profile.first_name || "—"}
              </div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Last Name</label>
              <div
                style={{
                  fontSize: "14px",
                  color: colors.text,
                  padding: "10px 0",
                }}
              >
                {profile.last_name || "—"}
              </div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Email</label>
              <div
                style={{
                  fontSize: "14px",
                  color: colors.text,
                  padding: "10px 0",
                }}
              >
                {profile.email || "—"}
              </div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Username</label>
              <div
                style={{
                  fontSize: "14px",
                  color: colors.slate,
                  padding: "10px 0",
                }}
              >
                {profile.username}
              </div>
            </div>
            <button
              onClick={() => setEditing(true)}
              style={{
                width: "100%",
                backgroundColor: colors.teal,
                color: "white",
                border: "none",
                padding: "12px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              Edit Profile
            </button>
          </>
        ) : (
          <>
            <div style={fieldStyle}>
              <label style={labelStyle}>First Name</label>
              <input
                style={inputStyle}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Last Name</label>
              <input
                style={inputStyle}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Email</label>
              <input
                style={inputStyle}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Username</label>
              <div
                style={{
                  fontSize: "13px",
                  color: colors.slate,
                  padding: "10px 0",
                }}
              >
                {profile.username}{" "}
                <span style={{ color: "#aaa" }}>(cannot be changed)</span>
              </div>
            </div>

            {saveError && (
              <div
                style={{
                  backgroundColor: "#fdecea",
                  border: `1px solid ${colors.red}`,
                  borderRadius: "6px",
                  padding: "10px 14px",
                  color: colors.red,
                  fontSize: "13px",
                  marginBottom: "16px",
                }}
              >
                {saveError}
              </div>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => {
                  setEditing(false);
                  setSaveError("");
                }}
                style={{
                  flex: 1,
                  backgroundColor: colors.lightGrey,
                  color: colors.slate,
                  border: `1px solid ${colors.border}`,
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1,
                  backgroundColor: saving ? colors.slate : colors.teal,
                  color: "white",
                  border: "none",
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Password section */}
      <div style={cardStyle}>
        <div
          style={{
            fontWeight: "700",
            fontSize: "15px",
            color: colors.darkGrey,
            marginBottom: "4px",
          }}
        >
          🔒 Password
        </div>
        <div
          style={{
            fontSize: "13px",
            color: colors.slate,
            marginBottom: "16px",
          }}
        >
          Change your login password
        </div>

        {passwordSuccess && (
          <div
            style={{
              backgroundColor: "#e8f8f1",
              border: `1px solid ${colors.green}`,
              borderRadius: "6px",
              padding: "10px 14px",
              color: colors.green,
              fontSize: "13px",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            ✅ Password changed successfully
          </div>
        )}

        {!showPasswordForm ? (
          <button
            onClick={() => setShowPasswordForm(true)}
            style={{
              width: "100%",
              backgroundColor: colors.lightGrey,
              color: colors.darkGrey,
              border: `1px solid ${colors.border}`,
              padding: "12px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Reset Password
          </button>
        ) : (
          <>
            <div style={fieldStyle}>
              <label style={labelStyle}>Current Password</label>
              <input
                style={inputStyle}
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>New Password</label>
              <input
                style={inputStyle}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                style={inputStyle}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
              />
            </div>

            {passwordError && (
              <div
                style={{
                  backgroundColor: "#fdecea",
                  border: `1px solid ${colors.red}`,
                  borderRadius: "6px",
                  padding: "10px 14px",
                  color: colors.red,
                  fontSize: "13px",
                  marginBottom: "16px",
                }}
              >
                {passwordError}
              </div>
            )}

            <div style={{ display: "flex", gap: "10px", marginBottom: "40px" }}>
              <button
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordError("");
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                style={{
                  flex: 1,
                  backgroundColor: colors.lightGrey,
                  color: colors.slate,
                  border: `1px solid ${colors.border}`,
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordChange}
                disabled={changingPassword}
                style={{
                  flex: 1,
                  backgroundColor: changingPassword
                    ? colors.slate
                    : colors.darkGrey,
                  color: "white",
                  border: "none",
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: changingPassword ? "not-allowed" : "pointer",
                }}
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
